'use client';
import { Portal } from '@headlessui/react';
import { AnimatePresence, motion } from 'motion/react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import Button from '@/components/v2/Button';
import { IconProps } from '@/icons/common';
import { cn } from '@/utils/twx';

import { useLangCheckState } from './hook';

const langText = {
  en: 'Please change the language to view content suitable for your current country',
  ko: '현재 계신 국가에 맞는 콘텐츠를 확인하려면 언어를 변경해주세요',
};

const CheckLang = () => {
  // 세션에 저장된 show 여부 (lang 선택 화면을 한번이라도 껏는지 여부)
  const [isShow, setIsShow] = useLangCheckState();
  // 유저 lang 과 pathname 에 따라 노출
  const [isShow2, setIsShow2] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  // 유저 lang 이 한국어 일때만 보여줌

  const [userLang, setUserLang] = useState('ko');

  const isUserLangKo = userLang.startsWith('ko') || userLang.startsWith('ko-KR');

  const close = () => {
    setIsShow(false);
  };

  const pathToKr = () => {
    if (pathname.includes('en')) {
      setIsShow(false);
      router.push(pathname.replace('/en', ''));
      return;
    }

    setIsShow(false);
  };

  const pathToEn = () => {
    if (!pathname.includes('en')) {
      setIsShow(false);
      router.push(`/en${pathname}`);
      return;
    }

    setIsShow(false);
  };

  useEffect(() => {
    const isEngPage = pathname.startsWith('/en');

    if (isUserLangKo) {
      if (isEngPage) {
        setIsShow2(true);
      }
      return;
    }

    if (!isEngPage) {
      setIsShow2(true);
      return;
    }
  }, [userLang, pathname, isUserLangKo]);

  useEffect(() => {
    if (!window || !navigator) return;
    const userLang = navigator.language;
    setUserLang(userLang);
  }, []);

  return (
    <>
      <Portal>
        <AnimatePresence>
          {isShow && isShow2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              className={cn(
                'fixed right-0 bottom-4.5 sm:right-[50px] sm:bottom-[50px] z-[43] px-4 sm:px-0',
              )}
            >
              <div className={'flex justify-end h-9'}>
                <button
                  className={
                    'bg-white size-9 rounded-full flex justify-center items-center shadow border'
                  }
                  onClick={close}
                >
                  <XIcon className={'size-5.5'} />
                </button>
              </div>

              <div className={'mt-2.5 rounded-[20px] bg-white px-[38px] py-7 shadow border'}>
                <p
                  className={cn(
                    'text-[14px] text-[#121212CC] leading-[1.4] text-center text-balance',
                    'sm:max-w-[180px]',
                  )}
                >
                  {langText[isUserLangKo ? 'ko' : 'en']}
                </p>

                <div className={'flex flex-col gap-1.5 mt-4.5'}>
                  <Button
                    className={'w-full rounded-full'}
                    variant={isUserLangKo ? 'gray' : 'outlinegray'}
                    onClick={pathToKr}
                  >
                    KR
                  </Button>
                  <Button
                    className={'w-full rounded-full'}
                    variant={isUserLangKo ? 'outlinegray' : 'gray'}
                    onClick={pathToEn}
                  >
                    EN
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    </>
  );
};

const XIcon = (props: IconProps) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10.9999 12.2962L16.1855 17.4818C16.3583 17.6488 16.5899 17.7412 16.8302 17.7391C17.0706 17.737 17.3005 17.6406 17.4705 17.4706C17.6404 17.3007 17.7368 17.0708 17.7389 16.8304C17.741 16.5901 17.6486 16.3585 17.4816 16.1856L12.296 11.0001L17.4816 5.81447C17.6486 5.64159 17.741 5.41003 17.7389 5.16969C17.7368 4.92934 17.6404 4.69943 17.4705 4.52947C17.3005 4.35951 17.0706 4.26311 16.8302 4.26102C16.5899 4.25893 16.3583 4.35133 16.1855 4.51831L10.9999 9.70389L5.8143 4.51831C5.64063 4.35545 5.41042 4.26656 5.17238 4.27042C4.93434 4.27429 4.70714 4.37061 4.53885 4.53902C4.37057 4.70742 4.27441 4.93469 4.27071 5.17273C4.26701 5.41078 4.35607 5.64092 4.51905 5.81447L9.70371 11.0001L4.51813 16.1856C4.43058 16.2702 4.36074 16.3713 4.3127 16.4832C4.26466 16.595 4.23937 16.7153 4.23832 16.837C4.23726 16.9587 4.26045 17.0794 4.30654 17.1921C4.35263 17.3048 4.4207 17.4071 4.50677 17.4932C4.59283 17.5792 4.69518 17.6473 4.80784 17.6934C4.92049 17.7395 5.0412 17.7627 5.16291 17.7616C5.28463 17.7606 5.40491 17.7353 5.51675 17.6872C5.62859 17.6392 5.72974 17.5694 5.8143 17.4818L10.9999 12.2962Z"
      fill="#5C5C61"
    />
  </svg>
);

export default CheckLang;
